import * as React from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { Col } from '@opendoor/atoms/box';
import { Loader } from '@opendoor/atoms/loader';
import { AccountForm, AppleAuthRedirectButton } from '@opendoor/auth-fe/forms';
import { MarketplacePropertyCategory_AccountCreatedEvent_AuthMethod as AccountCreatedAuthMethod } from '@opendoor/observability/pbs/journey/data/categories/marketplace_property_category_pb';
import { analytics, globalObservability } from '@opendoor/observability/slim';
import Cookies from 'js-cookie';

import { getLoggedInRedirectUrl } from '../../helpers/accountLoggedInRedirect';
import {
  clearMagicLinkAccountSession,
  isMagicLinkAccountSession,
} from '../../helpers/magicLinkAccountSession';
import { useObservability } from '../../helpers/observability';
import { useTimeBoundedFeatureFlagEvaluator } from '../helpers/hooks';
import {
  resolvePasswordlessRedirectUrl,
  sendPasswordlessSignInLink,
} from '../passwordless/passwordless';
import { pickLikedAddressUuid } from './accountFormPageParams';
import { AccountMagicLinkGate } from './AccountMagicLinkGate';
import { useAccountFormSession } from './useAccountFormSession';

const Sentry = globalObservability.getSentryClient();

const UNAUTHORIZED_STATUS_CODE = 401;

interface IAccountFormPageProps {
  config?: 'resaleClosingDashboard';
  redirectTo?: string | undefined;
  emailPrefill?: string | undefined;
  updatedPassword?: boolean;
}

const AccountFormPage: React.FC<IAccountFormPageProps> = ({
  config,
  emailPrefill,
  redirectTo,
  updatedPassword,
}) => {
  const {
    logIn,
    register,
    requestHumanStatus,
    hasGeneratedPassword,
    isNewHuman,
    loginRequestStatus,
    humanStatusRequestStatus,
    registrationRequestStatus,
    email,
    requestResetPassword,
    maybeFetchSessionInfo,
    loggedIn,
    serverLoginRedirect,
    sessionEmail,
    infoIsLoaded,
  } = useAccountFormSession();
  const { trackJourneyEvent } = useObservability();
  const evaluateAccountFormFlag = useTimeBoundedFeatureFlagEvaluator();
  const getSignInText = (config?: string) => {
    let loginHeaderText: string | undefined;
    let registrationHeaderText: string | undefined;
    switch (config) {
      case 'resaleClosingDashboard':
        loginHeaderText = 'Sign in to get back to your dashboard';
        registrationHeaderText = 'Sign up to get access to your dashboard';
        break;

      default:
        break;
    }

    return { loginHeaderText, registrationHeaderText };
  };

  const { loginHeaderText, registrationHeaderText } = getSignInText(config);

  const redirectIfLoggedIn = useCallback(() => {
    if (!loggedIn || isMagicLinkAccountSession()) {
      return;
    }

    analytics.trackEvent('login', 'redirect', undefined, {
      email,
      config,
      redirectTo,
      serverLoginRedirect,
    });

    window.location.assign(getLoggedInRedirectUrl(redirectTo, serverLoginRedirect));
  }, [config, email, loggedIn, redirectTo, serverLoginRedirect]);

  const completeMagicLinkGate = useCallback(() => {
    clearMagicLinkAccountSession();
    redirectIfLoggedIn();
  }, [redirectIfLoggedIn]);

  const wasLoggedIn = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    analytics.register({ login: Cookies.get('login-test') || 'no-cookie' });
    analytics.trackEvent('login', 'load', undefined, {
      email,
      config,
      redirectTo,
      emailPrefill,
    });
    maybeFetchSessionInfo();
    // oxlint-disable-next-line react/exhaustive-effect-dependencies, react/exhaustive-deps -- react19-baseline: pre-existing, do not copy. Fix the dependency array rather than suppressing. Add the missing dependencies rather than suppressing
  }, []);

  useEffect(() => {
    if (wasLoggedIn.current === true && !loggedIn) {
      clearMagicLinkAccountSession();
    }
    wasLoggedIn.current = loggedIn;
    if (loggedIn) {
      redirectIfLoggedIn();
    }
    // oxlint-disable-next-line react/exhaustive-effect-dependencies -- react19-baseline: pre-existing, do not copy. Fix the dependency array rather than suppressing
  }, [loggedIn, loginRequestStatus, redirectIfLoggedIn]);

  useEffect(() => {
    if (
      loginRequestStatus &&
      loginRequestStatus.error &&
      loginRequestStatus.statusCode !== UNAUTHORIZED_STATUS_CODE
    ) {
      Sentry.captureException?.(loginRequestStatus.error);
    }
  }, [loginRequestStatus]);

  const handlePasswordlessLoginRequest = useCallback(
    async (requestEmail: string) => {
      analytics.trackEvent('login', 'one-time-password', 'request', {
        email: requestEmail,
        redirectTo,
      });
      const redirectUrl = resolvePasswordlessRedirectUrl(redirectTo);
      const result = await sendPasswordlessSignInLink({
        input: {
          subject: { email: requestEmail },
          ...(redirectUrl ? { redirectUrl } : {}),
        },
      });
      if (!result.success) {
        analytics.trackEvent('login', 'one-time-password', 'error', {
          email: requestEmail,
          errors: result.errors,
        });
        return result;
      }

      analytics.trackEvent('login', 'one-time-password', 'success', { email: requestEmail });
      return result;
    },
    [redirectTo],
  );

  let headerText = loginHeaderText;

  if (updatedPassword) {
    headerText = 'Your password was successfully changed. Please sign in.';
  }
  const onRegister = (
    email?: string,
    name?: string,
    password?: string,
    phone?: string,
    googleIDToken?: string,
  ) => {
    register(email, name, password, phone, googleIDToken);
    analytics.trackEvent('session', 'register', 'account-creation', { email });
  };

  if (loggedIn && isMagicLinkAccountSession()) {
    return (
      <Col className="w-full items-center">
        <Col className="w-full max-w-120 items-center justify-center px-6 py-12 lg:px-0 lg:py-18">
          <AccountMagicLinkGate
            sessionEmail={sessionEmail}
            infoIsLoaded={infoIsLoaded}
            hasGeneratedPassword={hasGeneratedPassword}
            humanStatusRequestStatus={humanStatusRequestStatus}
            requestHumanStatus={requestHumanStatus}
            requestResetPassword={requestResetPassword}
            onComplete={completeMagicLinkGate}
          />
        </Col>
      </Col>
    );
  }

  return (
    <Col className="w-full items-center justify-center px-6 py-12 lg:px-0 lg:py-18">
      <AccountForm
        featureFlagProps={{
          evaluate: evaluateAccountFormFlag,
          loading: <Loader size="3xl" />,
        }}
        // Same evaluator, second key: the ordering is a setting of this surface
        // rather than an arm of the account-form slot, so it is read separately
        // and reaches both of that slot's arms.
        clientOrderedProvidersFlagProps={{ evaluate: evaluateAccountFormFlag }}
        accountFormProps={{
          loginAnalyticsPrefix: 'cosmos-login',
          analyticsName: 'cosmos-account-form',
          loginHeaderText: headerText,
          registrationHeaderText: registrationHeaderText,
          email: email || emailPrefill,
          hasGeneratedPassword: hasGeneratedPassword,
          error:
            loginRequestStatus.error ||
            humanStatusRequestStatus.error ||
            registrationRequestStatus.error,
          passwordLoading:
            !!serverLoginRedirect ||
            loginRequestStatus.isLoading ||
            registrationRequestStatus.isLoading,
          emailLoading: humanStatusRequestStatus.isLoading,
          isNewHuman: isNewHuman,
          onLogin: logIn,
          overrideHasSubmittedEmailCheckForLogin: !!emailPrefill,
          onRegister: onRegister,
          // Tracking must never break registration, and the form's completion
          // is a hard navigation — hence the try/catch and the flush.
          onRegistrationSuccess: ({ email: registeredEmail, method }) => {
            try {
              analytics.register({ email: registeredEmail });
              trackJourneyEvent(
                'marketplaceProperty',
                'accountCreated',
                {
                  addressUuid: pickLikedAddressUuid(redirectTo) ?? '',
                  authMethod:
                    method === 'passwordless'
                      ? AccountCreatedAuthMethod.LITE
                      : AccountCreatedAuthMethod.EMAIL_PASSWORD,
                },
                { flush: true },
              );
            } catch (err) {
              Sentry.captureException?.(err);
            }
          },
          onHumanStatusRequest: requestHumanStatus,
          requestResetPassword,
          onPasswordlessLoginRequest: handlePasswordlessLoginRequest,
          showGoogleAuth: true,
          loginRedirectTo: redirectTo,
          renderAppleAuth: () => (
            <Col className="items-center justify-center pt-6">
              <AppleAuthRedirectButton
                analyticsPrefix="cosmos-login"
                variant="secondaryInverse"
                redirectTo={redirectTo}
              />
            </Col>
          ),
        }}
      />
    </Col>
  );
};

export default AccountFormPage;
